import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { LogOut, Mail, Phone, RefreshCw, Lock } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function Login({ onLogin }) {
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, {
        withCredentials: true,
      });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border hairline bg-card p-8"
      >
        <div className="flex items-center gap-3">
          <LogoMark className="h-9 w-auto" />
          <div>
            <h1 className="font-grotesk text-lg font-semibold tracking-tight text-txt">
              Enquiry Inbox
            </h1>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-sub/70">
              BT Design Admin
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={googleLogin}
          data-testid="admin-google-login-button"
          className="btn-ghost mt-8 w-full justify-center"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
            <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
          </svg>
          Continue with Google
        </button>

        <div className="mt-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub/60">or</span>
          <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
        </div>

        <label className="mt-6 block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">Email</span>
          <input
            type="email"
            required
            value={creds.email}
            onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
            data-testid="admin-input-email"
            className="field mt-2"
          />
        </label>
        <label className="mt-4 block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub">Password</span>
          <input
            type="password"
            required
            value={creds.password}
            onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
            data-testid="admin-input-password"
            className="field mt-2"
          />
        </label>

        {error && (
          <p data-testid="admin-login-error" role="alert" className="mt-4 text-sm text-red-400">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="btn-primary mt-7 w-full justify-center disabled:opacity-60"
        >
          <Lock className="h-4 w-4" strokeWidth={2.2} />
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function EnquiryCard({ q }) {
  const date = q.created_at ? new Date(q.created_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "";
  return (
    <div
      data-testid="admin-enquiry-row"
      className="rounded-2xl border hairline bg-card p-6 transition-colors duration-300 hover:border-white/20"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-grotesk text-lg font-semibold tracking-tight text-txt">{q.name}</p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-sub/70">
            {date} · Ref {q.id?.slice(-8)?.toUpperCase()}
          </p>
        </div>
        {q.service && (
          <span className="rounded-full border border-cy/30 bg-cy/10 px-3 py-1 text-[11px] text-cy">
            {q.service}
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-sub transition-colors duration-300 hover:text-cy">
          <Mail className="h-4 w-4 text-cy/70" />{q.email}
        </a>
        {q.phone && (
          <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-sub transition-colors duration-300 hover:text-cy">
            <Phone className="h-4 w-4 text-cy/70" />{q.phone}
          </a>
        )}
        {q.company && <span className="text-sub">{q.company}</span>}
      </div>

      <p className="mt-4 border-t border-white/5 pt-4 text-sm leading-relaxed text-sub">{q.message}</p>
    </div>
  );
}

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || undefined);
  const [enquiries, setEnquiries] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch {
      setEnquiries([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (location.state?.user) {
      load();
      return;
    }
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        setUser(data);
        load();
      })
      .catch(() => setUser(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-ink" />;
  }

  if (user === null) {
    return (
      <Login
        onLogin={(u) => {
          setUser(u);
          load();
        }}
      />
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink pb-20">
      <header className="sticky top-0 z-10 border-b hairline bg-ink/80 backdrop-blur-xl">
        <div className="container-x flex h-[72px] items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-auto" />
            <div>
              <p className="font-grotesk text-base font-semibold tracking-tight text-txt">Enquiry Inbox</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-sub/70">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh-button"
              aria-label="Refresh enquiries"
              className="flex h-10 w-10 items-center justify-center rounded-full border hairline bg-white/[0.03] text-sub transition-colors duration-300 hover:border-cy/50 hover:text-cy"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="btn-ghost !px-5 !py-2 text-xs"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="container-x pt-12">
        <div className="flex items-baseline justify-between">
          <h1 className="font-grotesk text-2xl font-semibold tracking-tight text-txt sm:text-3xl">
            All enquiries
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sub/60" data-testid="admin-enquiry-count">
            {enquiries.length} total
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-4" data-testid="admin-enquiries-list">
          {enquiries.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-white/15 px-6 py-14 text-center text-sm text-sub">
              No enquiries yet. Submissions from the website contact form will appear here.
            </p>
          ) : (
            enquiries.map((q) => <EnquiryCard key={q.id} q={q} />)
          )}
        </div>
      </div>
    </div>
  );
}
